import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {QuestionOptions} from '../../../constants/question-options';
import {Partner} from '../../../model/proposal/partner';
import {FormBuilder, FormControl} from '@angular/forms';
import {Proposal} from '../../../model/proposal/proposal';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {CustomValidators} from '../../../validator/custom-validator';
import {ReplaySubject, Subject, takeUntil} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-partner-information-form',
    templateUrl: './partner-information-form.component.html',
    styleUrls: ['../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: PartnerInformationFormComponent }],
    standalone: false
})
export class PartnerInformationFormComponent extends ProposalFormBase implements OnInit, OnDestroy {
  @Input() partner: Partner;

  QuestionOptions = QuestionOptions;

  public filterCtrlCountry: FormControl<string> = new FormControl<string>('');
  public filterCtrlCountryCode: FormControl<string> = new FormControl<string>('');

  public filteredOptionsCountry: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  public filteredOptionsCountryCode: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

  protected destroy$ = new Subject<void>();

  countryOptions: string[] = [];
  countryCodeOptions: string[] = QuestionOptions.countryCodes;

  constructor(private readonly fb: FormBuilder, private readonly translate: TranslateService) {
    super();
  }

  ngOnInit() {
    if (this.partner != null) {
      this.formGroup.controls.name.patchValue(this.partner.name);
      this.formGroup.controls.street.patchValue(this.partner.street);
      this.formGroup.controls.houseNumber.patchValue(this.partner.houseNumber);
      this.formGroup.controls.postalCode.patchValue(this.partner.postalCode);
      this.formGroup.controls.location.patchValue(this.partner.location);
      this.formGroup.controls.countryCode.patchValue(this.partner.countryCode);
    }

    QuestionOptions.countryCodes.forEach(code =>
      this.countryOptions.push(this.translate.instant('countryCodes.' + code))
    );

    this.filteredOptionsCountry.next(this.countryOptions.slice());
    this.filteredOptionsCountryCode.next(this.countryCodeOptions.slice());

    this.filterCtrlCountryCode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCountries(this.filterCtrlCountryCode.value, this.countryCodeOptions, this.filteredOptionsCountryCode);
      });

    this.filterCtrlCountry.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCountries(this.filterCtrlCountry.value, this.countryOptions, this.filteredOptionsCountry);
      });
  }

  formGroup = this.fb.group({
    name: ['', [CustomValidators.notOnlyWhitespace]],
    street: ['', [CustomValidators.notOnlyWhitespace]],
    houseNumber: ['', [CustomValidators.notOnlyWhitespace]],
    postalCode: ['', [CustomValidators.notOnlyWhitespace]],
    location: ['', [CustomValidators.notOnlyWhitespace]],
    countryCode: ['', [CustomValidators.notOnlyWhitespace]],
  });

  updateProposal(proposal: Proposal): void {
    proposal.partner = new Partner(this.formGroup.value);
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.partner != null) {
      this.formGroup.controls.name.patchValue(proposal.partner.name);
      this.formGroup.controls.street.patchValue(proposal.partner.street);
      this.formGroup.controls.houseNumber.patchValue(proposal.partner.houseNumber);
      this.formGroup.controls.postalCode.patchValue(proposal.partner.postalCode);
      this.formGroup.controls.location.patchValue(proposal.partner.location);
      this.formGroup.controls.countryCode.patchValue(proposal.partner.countryCode);
    }
  }

  autoFillForEdit(proposal: Proposal) {
    if (proposal.partner != null) {
      this.formGroup.controls.name.patchValue(proposal.partner.name);
      this.formGroup.controls.street.patchValue(proposal.partner.street);
      this.formGroup.controls.houseNumber.patchValue(proposal.partner.houseNumber);
      this.formGroup.controls.postalCode.patchValue(proposal.partner.postalCode);
      this.formGroup.controls.location.patchValue(proposal.partner.location);
      this.formGroup.controls.countryCode.patchValue(proposal.partner.countryCode);
    }
  }

  filterCountries(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(country => country.toLowerCase().indexOf(search) > -1)
    );
  }

  getCountryCodeFromCountry(selectedCountry: string): string {
    const index = this.countryOptions.findIndex(country => country === selectedCountry);
    return index !== -1 ? this.countryCodeOptions[index] : '';
  }

  reset() {
    this.formGroup.reset();
  }
}
